import { T } from "@supersig/validate";
import { cssTypeMap } from "./css";
import { swTextResourceReference } from "./resource";
import { variableValueTypes } from "./variables";
/** @internal */ function idValidator(prefix) {
    return T.string.refine((id)=>{
        if (!id.startsWith(`${prefix}:`)) {
            throw new Error(`${prefix} ID must start with "${prefix}:"`);
        }
        return id;
    });
}
const stateIdValidator = idValidator("state");
const _PropertyTypeText = T.object({
    type: T.literal("property-text"),
    value: T.string,
    rendering: T.union("type", {
        literal: T.object({
            type: T.literal("literal")
        }),
        liquid: T.object({
            type: T.literal("liquid"),
            requiredStateIds: T.arrayOf(stateIdValidator)
        })
    }),
    // Localization data for text content
    localization: T.dict(// todo: actually type this against all possible language codes
    T.string, // translation data
    T.string).optional(),
    // A link to a text resource (for localization)
    resourceReference: swTextResourceReference.optional()
});
const PropertyTypeText = _PropertyTypeText;
const PropertyTypeImage = T.object({
    type: T.literal("property-image"),
    src: T.string,
    rendering: T.union("type", {
        literal: T.object({
            type: T.literal("literal")
        }),
        liquid: T.object({
            type: T.literal("liquid"),
            requiredStateIds: T.arrayOf(stateIdValidator)
        })
    }).optional()
});
const PropertyTypeIcon = T.object({
    type: T.literal("property-icon"),
    name: T.string,
    strokeLinecap: T.string,
    strokeLinejoin: T.string,
    strokeColor: T.string,
    strokeWidth: T.number,
    width: T.number,
    height: T.number
});
const PropertyTypeStack = T.object({
    type: T.literal("property-stack"),
    axis: T.string,
    reverse: T.boolean,
    crossAxisAlignment: T.string,
    mainAxisDistribution: T.string,
    wrap: T.string,
    gap: T.string,
    scroll: T.setEnum(new Set([
        "none",
        "normal",
        "paging",
        "infinite"
    ])).optional(),
    snapPosition: T.setEnum(new Set([
        "start",
        "center",
        "end"
    ])).optional(),
    autoAdvancePageEnabled: T.setEnum(new Set([
        "enabled",
        "disabled"
    ])).optional(),
    infiniteScrollSpeed: T.number.optional(),
    autoAdvancePageDelayS: T.string.optional()
});
const PropertyTypeNavigation = T.object({
    type: T.literal("property-navigation"),
    transition: T.setEnum(new Set([
        "none",
        "push",
        "fade",
        "slide"
    ]))
});
const PropertyTypeVideo = T.object({
    type: T.literal("property-video"),
    src: T.string,
    thumbnail: T.string.optional(),
    sources: T.arrayOf(T.object({
        src: T.string,
        type: T.string
    })).optional(),
    rendering: T.union("type", {
        literal: T.object({
            type: T.literal("literal")
        }),
        liquid: T.object({
            type: T.literal("liquid"),
            requiredStateIds: T.arrayOf(stateIdValidator)
        })
    }).optional(),
    autoplay: T.boolean,
    loop: T.boolean,
    muted: T.boolean,
    controls: T.boolean
});
const PropertyTypeLottie = T.object({
    type: T.literal("property-lottie"),
    src: T.string,
    rendering: T.union("type", {
        literal: T.object({
            type: T.literal("literal")
        }),
        liquid: T.object({
            type: T.literal("liquid"),
            requiredStateIds: T.arrayOf(stateIdValidator)
        })
    }).optional(),
    autoplay: T.boolean,
    loop: T.boolean,
    controls: T.boolean,
    keepLastFrame: T.boolean,
    speed: T.number
});
const PropertyTypeDrawer = T.object({
    type: T.literal("property-drawer"),
    dismissible: T.boolean,
    detents: T.arrayOf(cssTypeMap["css-number-unit"]),
    backgroundInteractive: T.boolean
});
const PropertyTypeInput = T.object({
    type: T.literal("property-input"),
    placeholder: T.string,
    keyboardType: T.setEnum(new Set([
        "text",
        "email",
        "number",
        "tel",
        "url",
        "password"
    ]))
});
// set state operations (set, increment-by and toggle)
const SetStateOperation = T.union("type", {
    ["set"]: T.object({
        type: T.literal("set")
    }),
    ["increment"]: T.object({
        type: T.literal("increment")
    }),
    ["decrement"]: T.object({
        type: T.literal("decrement")
    }),
    ["toggle"]: T.object({
        type: T.literal("toggle")
    })
});
// click behaviors
export const ActionSetState = T.object({
    type: T.literal("set-state"),
    stateId: stateIdValidator,
    operation: SetStateOperation.optional(),
    value: variableValueTypes.optional()
});
export const ActionOpenUrl = T.object({
    type: T.literal("open-url"),
    url: T.string,
    urlType: T.setEnum(new Set([
        "in-app-browser",
        "deep-link",
        "external"
    ])),
    rendering: T.union("type", {
        literal: T.object({
            type: T.literal("literal")
        }),
        liquid: T.object({
            type: T.literal("liquid"),
            requiredStateIds: T.arrayOf(stateIdValidator)
        })
    }).optional()
});
export const ActionClose = T.object({
    type: T.literal("close")
});
export const ActionRestore = T.object({
    type: T.literal("restore")
});
export const ActionCustomInApp = T.object({
    type: T.literal("custom-in-app"),
    data: T.string
});
export const ActionCustomPlacement = T.object({
    type: T.literal("custom-placement"),
    name: T.string
});
export const ActionSetSelectedProductIndex = T.object({
    type: T.literal("set-product-index"),
    index: T.number
});
export const ActionPurchaseProductByIndex = T.object({
    type: T.literal("by-index"),
    index: T.number
});
export const ActionPurchaseProductBySelected = T.object({
    type: T.literal("by-selected")
});
export const ActionPurchaseProduct = T.union("type", {
    ["by-index"]: ActionPurchaseProductByIndex,
    ["by-selected"]: ActionPurchaseProductBySelected
});
export const ActionPurchase = T.object({
    type: T.literal("purchase"),
    reference: ActionPurchaseProduct
});
export const PropertyAction = T.union("type", {
    ["set-state"]: ActionSetState,
    ["open-url"]: ActionOpenUrl,
    ["close"]: ActionClose,
    ["restore"]: ActionRestore,
    ["purchase"]: ActionPurchase,
    ["custom-in-app"]: ActionCustomInApp,
    ["custom-placement"]: ActionCustomPlacement,
    ["set-product-index"]: ActionSetSelectedProductIndex
});
export const ActionExecution = T.object({
    type: T.literal("action-execution"),
    action: PropertyAction,
    id: T.string
});
const gameControllerButton = T.literalEnum("l1", "l2", "r1", "r2", "a", "b", "x", "y", "menu", "options");
export const PropertyTypeClickBehavior = T.object({
    type: T.literal("property-click-behavior"),
    animation: T.setEnum(new Set([
        "none",
        "scale-in-out",
        "scale-out-in",
        "fade-out-in"
    ])),
    gameControllerButton: gameControllerButton.optional(),
    backButton: T.boolean.optional(),
    clickActions: T.arrayOf(ActionExecution)
});
export const CustomCSSProperty = T.object({
    type: T.literal("custom-css-property"),
    id: T.string,
    property: T.string,
    value: T.string
});
export const PropertyTypeCustomCSS = T.object({
    type: T.literal("property-custom-css"),
    properties: T.arrayOf(CustomCSSProperty)
});
export const propertyTypeMap = {
    // "property-text-literal": PropertyTypeTextLiteral,
    // "property-text-liquid": PropertyTypeTextLiquid,
    "property-text": PropertyTypeText,
    "property-image": PropertyTypeImage,
    "property-icon": PropertyTypeIcon,
    "property-video": PropertyTypeVideo,
    "property-stack": PropertyTypeStack,
    "property-lottie": PropertyTypeLottie,
    "property-click-behavior": PropertyTypeClickBehavior,
    "property-custom-css": PropertyTypeCustomCSS,
    "property-navigation": PropertyTypeNavigation,
    "property-drawer": PropertyTypeDrawer,
    "property-input": PropertyTypeInput
};
export const propertyTypes = T.union("type", propertyTypeMap);
